import { VideoHTMLAttributes, useEffect, useRef } from "react";

interface TrimmedVideoProps
  extends Omit<VideoHTMLAttributes<HTMLVideoElement>, "src"> {
  src: string;
  /** Absolute time (seconds) where playback should always start / loop back to. */
  trimStart?: number;
  /** Absolute time (seconds) where playback should loop back to trimStart. `null` = natural end. */
  trimEnd?: number | null;
}

/**
 * <video> wrapper that enforces a virtual trim window [trimStart, trimEnd].
 * Whenever currentTime falls outside the window it snaps back to trimStart,
 * so the source mp4 effectively plays a sub-range — both on load and on every loop.
 */
export default function TrimmedVideo({
  src,
  trimStart = 0,
  trimEnd = null,
  ...rest
}: TrimmedVideoProps) {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;

    const enforce = () => {
      if (v.currentTime < trimStart - 0.001) {
        v.currentTime = trimStart;
        return;
      }
      if (trimEnd !== null && v.currentTime >= trimEnd) {
        v.currentTime = trimStart;
      }
    };

    v.addEventListener("loadedmetadata", enforce);
    v.addEventListener("seeking", enforce);
    v.addEventListener("timeupdate", enforce);
    v.addEventListener("ended", enforce);

    if (v.readyState >= 1) enforce();

    return () => {
      v.removeEventListener("loadedmetadata", enforce);
      v.removeEventListener("seeking", enforce);
      v.removeEventListener("timeupdate", enforce);
      v.removeEventListener("ended", enforce);
    };
  }, [trimStart, trimEnd]);

  return <video ref={ref} src={src} {...rest} />;
}